import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash-es/debounce';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  searchReportPermissionUsers,
  type IAuthorizedUser,
} from '@op/services/report';

const PAGE_SIZE = 20;

export function useSearchPermissionUsers() {
  const { wrapperCancelRequest, loading: searchLoading } =
    useCancelRequestHook();
  const [searchOptions, setSearchOptions] = useState<IAuthorizedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const wrapperCancelRequestRef = useRef(wrapperCancelRequest);
  wrapperCancelRequestRef.current = wrapperCancelRequest;

  const keywordRef = useRef('');
  const pageRef = useRef(1);

  const hasMore = searchOptions.length < total;

  const searchUsers = useMemo(
    () =>
      debounce((keyword: string) => {
        const trimmed = keyword.trim();
        if (!trimmed) {
          keywordRef.current = '';
          pageRef.current = 1;
          setSearchOptions([]);
          setTotal(0);
          return;
        }

        keywordRef.current = trimmed;
        pageRef.current = 1;

        wrapperCancelRequestRef
          .current(searchReportPermissionUsers, {
            keyword: trimmed,
            page: 1,
            page_size: PAGE_SIZE,
          })
          .then((data) => {
            if (!data || keywordRef.current !== trimmed) return;
            setSearchOptions(data.list || []);
            setTotal(data.total || 0);
            pageRef.current = 1;
          })
          .catch(() => {
            if (keywordRef.current !== trimmed) return;
            setSearchOptions([]);
            setTotal(0);
          });
      }, 400),
    [],
  );

  const loadMore = useCallback(() => {
    const trimmed = keywordRef.current;
    if (!trimmed || searchOptions.length >= total) return;
    if (searchLoading || loadMoreLoading) return;

    const nextPage = pageRef.current + 1;
    setLoadMoreLoading(true);

    searchReportPermissionUsers({
      data: {
        keyword: trimmed,
        page: nextPage,
        page_size: PAGE_SIZE,
      },
    })
      .then((data) => {
        if (!data || keywordRef.current !== trimmed) return;
        setSearchOptions((prev) => {
          const existingIds = new Set(prev.map((item) => item.user_id));
          const newItems = (data.list || []).filter(
            (item) => !existingIds.has(item.user_id),
          );
          return [...prev, ...newItems];
        });
        setTotal(data.total || 0);
        pageRef.current = nextPage;
      })
      .catch(() => {})
      .finally(() => {
        setLoadMoreLoading(false);
      });
  }, [loadMoreLoading, searchLoading, searchOptions.length, total]);

  const clearSearch = useCallback(() => {
    searchUsers.cancel();
    keywordRef.current = '';
    pageRef.current = 1;
    setSearchOptions([]);
    setTotal(0);
    setLoadMoreLoading(false);
  }, [searchUsers]);

  useEffect(() => {
    return () => {
      searchUsers.cancel();
    };
  }, [searchUsers]);

  return {
    searchOptions,
    searchLoading,
    searchUsers,
    clearSearch,
    loadMore,
    hasMore,
    loadMoreLoading,
  };
}
